/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useTranslation } from "react-i18next";
import {
	CapacityMapping,
	ContainerBatch,
	ContainerLot,
} from "../../../types/mediaBatchDispense.types";
import { DefaultContainer } from "../../../types/mediaBatchCommon.types";

interface Props {
	containerBatches: ContainerBatch[];
	containerLots: ContainerLot[];
	containerDimensions: CapacityMapping[];

	selectedContainerBatch: ContainerBatch | null;
	selectedContainerLot: ContainerLot | null;
	selectedContainerDimension: CapacityMapping | null;

	setSelectedContainerBatch: (batch: ContainerBatch) => void;
	setSelectedContainerLot: (lot: ContainerLot) => void;
	defaultContainer?: DefaultContainer;
	handleContainerDimensionChange: (id: string) => void;
	errors: {
		containerDimension: string;
		containerBatch: string;
		containerLot: string;
		containerSelection: string;
	};
}

const ContainerSelectionForm = ({

	containerBatches,
	containerLots,
	containerDimensions,

	selectedContainerBatch,
	selectedContainerLot,
	selectedContainerDimension,
	setSelectedContainerBatch,
	setSelectedContainerLot,
	handleContainerDimensionChange,

	defaultContainer,
	errors,
}: Props) => {
	const { t } = useTranslation();

	return (
		<div className="grid grid-cols-4 gap-1">
			{/* 1. Container Type */}
			<div className="space-y-2">
				<label className="flex min-h-[16px] items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
					{t("mediaBatchInit.dispenseEntry.containerType")}
					<span className="material-symbols-outlined text-[10px] text-slate-300">
						info
					</span>
				</label>
				<div className="relative">
					<select
						className="w-full appearance-none rounded-xl border border-slate-200 bg-slate-50 pl-2 pr-10 py-2 text-[12px] text-[#0F172A] outline-none focus:ring-4 focus:ring-blue-500/5"
					>
						<option value="" disabled selected>
							{defaultContainer?.container_type_name ?? ""}
						</option>
					</select>
					<span className="text-[15px] material-symbols-outlined pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
						expand_more
					</span>
				</div>
			</div>

			{/* 2. Container Dimensions (Newly Added) */}
			<div className="space-y-2">
				<label className="flex min-h-[16px] items-center text-[10px] font-black uppercase tracking-widest text-slate-400">
					<span>
						{t("mediaBatchInit.dispenseEntry.containerDimensions")}
						<span className="ml-0.5 text-red-500">*</span>
					</span>
				</label>
				<div className="relative">
					<select
						value={selectedContainerDimension?.id ?? ""}
						onChange={(e) =>
							handleContainerDimensionChange(e.target.value)
						}
						className="w-full appearance-none rounded-xl border border-slate-200 bg-slate-50 pl-2 pr-10 py-2 text-[12px] text-[#0F172A] outline-none focus:ring-4 focus:ring-blue-500/5"
					>
						<option value="" disabled>
							{t("mediaBatchInit.dispenseEntry.selectDimension")}
						</option>
						{containerDimensions.map((c) => (
							<option key={c.id} value={c.id}>
								{/* Note: Update 'c.name' below to whichever property CapacityMapping uses for display */}
								{c.size}
							</option>
						))}
					</select>
					<span className="text-[15px] material-symbols-outlined pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
						expand_more
					</span>
				</div>
				{errors.containerDimension && (<p className="text-[11px] text-red-500">{errors.containerDimension}</p>)}
			</div>

			{/* 3. Container Batch */}
			<div className="space-y-2">
				<label className="flex min-h-[16px] items-center text-[10px] gap-2 font-black uppercase tracking-widest text-slate-400">
					<span>
						{t("mediaBatchInit.dispenseEntry.containerBatch")}
						<span className="ml-0.5 text-red-500">*</span>
					</span>
				</label>
				<div className="relative">
					<select
						value={selectedContainerBatch?.id ?? ""}
						onChange={(e) =>
							setSelectedContainerBatch(
								containerBatches.find((c) => c.id === e.target.value)!,
							)
						}
						className="w-full appearance-none rounded-xl border border-slate-200 bg-slate-50 pl-2 pr-10 py-2 text-[12px] text-[#0F172A] outline-none focus:ring-4 focus:ring-blue-500/5"
					>
						<option value="" disabled>
							{t("mediaBatchInit.dispenseEntry.selectContainerBatch")}
						</option>
						{containerBatches.map((c) => (
							<option key={c.id} value={c.id}>
								{c.batch_no}
							</option>
						))}
					</select>
					<span className="text-[15px] material-symbols-outlined pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
						expand_more
					</span>
				</div>
				{errors.containerBatch && (<p className="text-[11px] text-red-500">{errors.containerBatch}</p>)}
			</div>

			{/* 4. Container Lot */}
			<div className="space-y-2">
				<label className="flex min-h-[16px] items-center text-[10px] gap-2 font-black uppercase tracking-widest text-slate-400">
					<span>
					{t("mediaBatchInit.dispenseEntry.containerLot")}
					<span className="ml-0.5 text-red-500">*</span>
				</span>
				</label>
				<div className="relative">
					<select
						value={selectedContainerLot?.id ?? ""}
						onChange={(e) =>
							setSelectedContainerLot(
								containerLots.find((c) => c.id === e.target.value)!,
							)
						}
						className="w-full appearance-none rounded-xl border border-slate-200 bg-slate-50 pl-2 pr-10 py-2 text-[12px] text-[#0F172A] outline-none focus:ring-4 focus:ring-blue-500/5"
					>
						<option value="" disabled>
							{t("mediaBatchInit.dispenseEntry.selectContainerLot")}
						</option>
						{containerLots.map((c) => (
							<option key={c.id} value={c.id}>
								{c.lot_code}
							</option>
						))}
					</select>
					<span className="text-[15px] material-symbols-outlined pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
						expand_more
					</span>
				</div>
				{errors.containerLot && (<p className="text-[11px] text-red-500">{errors.containerLot}</p>)}
			</div>
		</div>
	);
}

export default ContainerSelectionForm;
